// components/brand/BrandForm/index.js - Updated with verified and acceptsCrypto fields
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  addDoc, 
  doc, 
  getDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  writeBatch 
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../context/AuthContext';

// Import sub-components
import BasicInfo from './BasicInfo';
import ImageUpload from './ImageUpload';
import PromotionSection from './PromotionSection';

// Constants
const MAX_FILE_SIZE = 500 * 1024; // 500KB

function BrandForm({ brandId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    welcomeBonus: '',
    rating: 4.0,
    logoImage: '',
    coverImage: '',
    websiteUrl: '',
    verified: false,       // Added verified field with default false
    acceptsCrypto: false,  // Added acceptsCrypto field with default false
    promotions: []
  });
  
  const [slugEdited, setSlugEdited] = useState(false);
  
  // Generate slug from name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')  // Remove special characters
      .replace(/\s+/g, '-')      // Replace spaces with hyphens
      .replace(/--+/g, '-')      // Replace multiple hyphens with single hyphen
      .trim();                   // Trim whitespace
  };
  
  // Check if slug is unique
  const checkSlugUniqueness = async (slug, excludeId = null) => {
    try {
      const slugQuery = query(collection(db, "brands"), where("slug", "==", slug));
      const querySnapshot = await getDocs(slugQuery);
      
      if (querySnapshot.empty) {
        return true; // Slug is unique
      }
      
      // If editing an existing brand, check if the found slug belongs to the current brand
      if (excludeId) {
        // Check if any of the documents with this slug have a different ID than our current brand
        const matchingDocWithDifferentId = querySnapshot.docs.some(doc => doc.id !== excludeId);
        return !matchingDocWithDifferentId; // If there are no docs with different IDs, the slug is unique for this edit
      }
      
      return false; // Slug is not unique for a new brand
    } catch (error) {
      console.error("Error checking slug uniqueness:", error);
      return false; // Assume not unique in case of error
    }
  };
  
  // Generate a unique slug by adding a number if needed
  const generateUniqueSlug = useCallback(async (baseName, excludeId = null) => {
    const baseSlug = generateSlug(baseName);
    let slug = baseSlug;
    let isUnique = await checkSlugUniqueness(slug, excludeId);
    let counter = 1;
    
    // If not unique, append a number and check again
    while (!isUnique) {
      slug = `${baseSlug}-${counter}`;
      isUnique = await checkSlugUniqueness(slug, excludeId);
      counter++;
      
      // Safety check to prevent infinite loops
      if (counter > 100) {
        throw new Error("Could not generate a unique slug after many attempts");
      }
    }
    
    return slug;
  }, []);
  
  // Initialize promotions array
  const initializePromotions = () => {
    // Create an empty array for promotions
    return [];
  };
  
  // Load existing data if in edit mode
  useEffect(() => {
    const fetchBrand = async () => {
      if (!brandId) {
        // Initialize empty promotions for new brand
        setFormData(prev => ({
          ...prev,
          promotions: initializePromotions()
        }));
        return;
      }
      
      try {
        setLoading(true);
        const brandDocRef = doc(db, "brands", brandId);
        const brandSnapshot = await getDoc(brandDocRef);
        
        if (brandSnapshot.exists()) {
          const brandData = brandSnapshot.data();
          console.log("Fetched brand data:", brandData);
          
          // Fetch promotions from subcollection
          const promotionsCollectionRef = collection(brandDocRef, "promotions");
          const promotionsSnapshot = await getDocs(promotionsCollectionRef);
          
          let promotions = [];
          promotionsSnapshot.forEach(doc => {
            promotions.push({
              id: doc.id,
              ...doc.data()
            });
          });
          
          // If no slug exists in the db, generate one
          let slug = brandData.slug;
          if (!slug) {
            slug = await generateUniqueSlug(brandData.name, brandId);
          }
          
          setFormData({
            ...formData, // Start with defaults for new fields
            ...brandData, // Override with existing data
            // Ensure rating is a number
            rating: typeof brandData.rating === 'string' ? parseFloat(brandData.rating) : brandData.rating,
            slug: slug,
            promotions, // Replace or set promotions
            websiteUrl: brandData.websiteUrl || '', // Handle website URL field
            verified: !!brandData.verified, // Ensure boolean type
            acceptsCrypto: !!brandData.acceptsCrypto // Ensure boolean type
          });
        } else {
          console.error("Brand not found");
          setError("Brand not found. Please try again or create a new brand.");
          // Initialize empty promotions even if brand not found
          setFormData(prev => ({
            ...prev,
            promotions: initializePromotions()
          }));
        }
      } catch (error) {
        console.error("Error fetching brand:", error);
        setError("Failed to load brand data. Please try again later.");
        // Initialize empty promotions on error
        setFormData(prev => ({
          ...prev,
          promotions: initializePromotions()
        }));
      } finally {
        setLoading(false);
      }
    };
    
    fetchBrand();
  }, [brandId, generateUniqueSlug]);
  
  // Update basic info
  const handleBasicInfoChange = (newData) => {
    setFormData(prev => ({
      ...prev,
      ...newData
    }));
    
    // If name is updated and slug hasn't been manually edited, update slug
    if (newData.name && !slugEdited) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(newData.name)
      }));
    }
  };
  
  // Update slug edit state
  const handleSlugEditToggle = (isEdited) => {
    setSlugEdited(isEdited);
  };
  
  // Reset slug to match name
  const handleResetSlug = () => {
    setSlugEdited(false);
    setFormData(prev => ({
      ...prev,
      slug: generateSlug(prev.name)
    }));
  };
  
  // Update image fields
  const handleImageChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Update promotion fields
  const handlePromotionChange = (updatedPromotions) => {
    setFormData(prev => ({
      ...prev,
      promotions: updatedPromotions
    }));
  };
  
  // Validate website URL
  const validateWebsiteUrl = (url) => {
    if (!url) return true; // Empty URL is valid (not required)
    
    try {
      new URL(url); // This will throw an error if URL is invalid
      return true;
    } catch {
      return false;
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setError('You must be logged in to create or edit a brand');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Validate slug
      if (!formData.slug) {
        throw new Error("Brand URL slug is required");
      }
      
      // Validate website URL if present
      if (formData.websiteUrl && !validateWebsiteUrl(formData.websiteUrl)) {
        throw new Error("Website URL is invalid. Please enter a valid URL including https://");
      }
      
      // Check slug uniqueness - make sure to pass brandId for edit mode
      const isSlugUnique = await checkSlugUniqueness(formData.slug, brandId);
      if (!isSlugUnique) {
        throw new Error("This URL slug is already in use. Please choose a different one.");
      }
      
      // Images are now Firebase Storage URLs, no size validation needed
      
      // Prepare brand data without promotions
      const brandDataToSave = {
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        description: formData.description.trim(),
        welcomeBonus: formData.welcomeBonus.trim(),
        rating: parseFloat(formData.rating), // Ensure it's a number
        logoImage: formData.logoImage || '', // Already compressed
        coverImage: formData.coverImage || '', // Already compressed
        websiteUrl: formData.websiteUrl.trim(), // Include website URL
        verified: !!formData.verified, // Ensure boolean type for verified field
        acceptsCrypto: !!formData.acceptsCrypto // Ensure boolean type for acceptsCrypto field
      };
      
      console.log('Brand data to be saved:', {
        ...brandDataToSave,
        logoImage: brandDataToSave.logoImage ? `[Firebase Storage URL: ${brandDataToSave.logoImage}]` : '',
        coverImage: brandDataToSave.coverImage ? `[Firebase Storage URL: ${brandDataToSave.coverImage}]` : ''
      });
      
      let brandRef;
      
      if (brandId) {
        // Update existing brand
        console.log(`Updating brand with ID: ${brandId}`);
        brandRef = doc(db, 'brands', brandId);
        await updateDoc(brandRef, {
          ...brandDataToSave,
          updatedAt: new Date()
        });
      } else {
        // Add document to 'brands' collection in Firestore
        console.log("Creating new brand");
        const brandsCollectionRef = collection(db, 'brands');
        const docRef = await addDoc(brandsCollectionRef, {
          ...brandDataToSave,
          createdBy: user.uid, // This is crucial
          createdAt: new Date()
        });
        brandId = docRef.id;
        brandRef = docRef;
      }
      
      // Now handle promotions using a subcollection
      console.log("Saving promotions to subcollection...");
      
      // Create a batch for multiple operations
      const batch = writeBatch(db);
      
      // Create a map of existing promotions by ID for comparison
      const existingPromotionsRef = collection(brandRef, "promotions");
      const existingPromotionsSnapshot = await getDocs(existingPromotionsRef);
      const existingPromotionIds = new Set();
      
      // Add all existing promotion IDs to the set
      existingPromotionsSnapshot.forEach(doc => {
        existingPromotionIds.add(doc.id);
      });
      
      // For each promotion in the form
      for (const promotion of formData.promotions) {
        // Filter out empty promotions
        if (!promotion.image && !promotion.title && !promotion.description) {
          console.log("Skipping empty promotion:", promotion);
          continue;
        }
        
        // Check if this is an existing promotion or new one
        if (promotion.id && existingPromotionIds.has(promotion.id)) {
          // Update existing promotion
          const promotionRef = doc(brandRef, "promotions", promotion.id);
          batch.update(promotionRef, {
            ...promotion,
            updatedAt: new Date()
          });
          
          // Remove from set so we know it's been processed
          existingPromotionIds.delete(promotion.id);
        } else {
          // Create new promotion document with auto-generated ID
          const newPromotionRef = doc(collection(brandRef, "promotions"));
          batch.set(newPromotionRef, {
            ...promotion,
            id: promotion.id || newPromotionRef.id, // Use existing ID or new one
            createdAt: new Date(),
            updatedAt: new Date()
          });
        }
      }
      
      // Delete any promotions that exist in the database but are not in the form data
      existingPromotionIds.forEach(id => {
        const promotionToDeleteRef = doc(brandRef, "promotions", id);
        batch.delete(promotionToDeleteRef);
      });
      
      // Commit all the changes as a single batch
      await batch.commit();
      console.log("Promotions updated successfully");
      
      // Show success message
      setSaveSuccess(true);
      
      // Navigate to brand list after a delay
      setTimeout(() => {
        navigate('/mybrands');
      }, 1500);
      
    } catch (error) {
      console.error('Error saving brand:', error);
      setError(`Failed to save brand: ${error.message}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading && brandId) {
    return (
      <div className="p-4 max-w-6xl mx-auto flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Loading brand data...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="pb-8 p-4 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">
        {brandId ? 'Edit Brand' : 'Create New Brand'}
      </h2>
      
      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Success Message */}
      {saveSuccess && (
        <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                {brandId ? 'Brand updated successfully!' : 'Brand created successfully!'}
              </p>
              <p className="text-sm text-green-700 mt-1">
                Redirecting to your brands...
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* User Info Alert */}
      {!user && (
        <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-yellow-800">
                You need to be logged in to create or edit brands.
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-blue-600 p-4 text-white">
          <h3 className="text-xl font-semibold">Brand Information</h3>
          <p className="text-blue-100 text-sm">
            {brandId
              ? 'Edit your brand details below'
              : 'Fill out the form below to create a new brand listing'}
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information Section */}
          <BasicInfo 
            data={formData} 
            onChange={handleBasicInfoChange}
            slugEdited={slugEdited}
            onSlugEditToggle={handleSlugEditToggle}
            onResetSlug={handleResetSlug}
          />
          
          {/* Images Section */}
          <ImageUpload
            logoImage={formData.logoImage}
            coverImage={formData.coverImage}
            onImageChange={handleImageChange}
            brandId={brandId}
            brandName={formData.name}
          />

          {/* Promotions Section */}
          <PromotionSection
            promotions={formData.promotions}
            onChange={handlePromotionChange}
            brandId={brandId}
            brandName={formData.name}
          />
          
          {/* Submit Button */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/mybrands')}
              className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 py-3 px-4 rounded-lg text-white font-semibold ${
                loading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {brandId ? 'Updating...' : 'Creating...'}
                </span>
              ) : (
                brandId ? 'Update Brand' : 'Create Brand'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BrandForm;